import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Wand2, X } from 'lucide-react'

import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { indexarModelo } from '@core/layout/search/buildIndex'
import type { EntradaPesquisa } from '@core/layout/search/searchIndex'
import {
  type AccaoLente,
  type EixoLente,
  type Lente,
  type OperadorLente,
  type RegraLente,
  avaliarLente,
  corSeguinte,
  gravarLentes,
  lenteAutomatica,
  lerLentes,
} from '@viewer/lens/lensRules'
import { aplicarLente, limparLente } from '@viewer/lens/applyLens'

const EIXOS: { valor: EixoLente; label: string }[] = [
  { valor: 'classe', label: 'Classe IFC' },
  { valor: 'nome', label: 'Nome' },
  { valor: 'modelo', label: 'Modelo' },
]

const OPERADORES: { valor: OperadorLente; label: string }[] = [
  { valor: 'igual', label: 'é igual a' },
  { valor: 'comeca', label: 'começa por' },
  { valor: 'contem', label: 'contém' },
  { valor: 'vazio', label: 'está vazio' },
]

const ACCOES: { valor: AccaoLente; label: string }[] = [
  { valor: 'colorir', label: 'Colorir' },
  { valor: 'transparente', label: 'Apagar' },
  { valor: 'esconder', label: 'Esconder' },
  { valor: 'fantasma', label: 'Fantasma' },
]

function lenteVazia(): Lente {
  return { id: `l-${Date.now()}`, nome: 'Lente nova', regras: [] }
}

/** Painel das lentes: ver o modelo por regras.
 *
 *  Complementa o "Colorir por" (uma dimensão, colore tudo) e o Filtro
 *  (esconde, não colore). A lente faz as duas coisas com várias
 *  regras, e a primeira que casa ganha. */
export default function LensPanel({ aoFechar }: { aoFechar: () => void }) {
  const models = useAppStore((s) => s.models)
  const [lente, setLente] = useState<Lente>(lenteVazia)
  const [guardadas, setGuardadas] = useState<Lente[]>(lerLentes)
  const [lidas, setLidas] = useState<{
    chave: string
    entradas: EntradaPesquisa[]
  }>({ chave: '', entradas: [] })
  const [aLer, setALer] = useState(false)
  const [activa, setActiva] = useState(false)
  const [erro, setErro] = useState<string | null>(null)

  // Os elementos lidos valem para os modelos que estavam carregados
  // quando foram lidos. Abrir ou fechar um IFC invalida-os: sem isto o
  // modelo novo nunca entrava na lente e a legenda contava o antigo.
  const chaveModelos = models.map((m) => `${m.id} ${m.fileName}`).join('\n')
  const entradas = useMemo(
    () => (lidas.chave === chaveModelos ? lidas.entradas : []),
    [lidas, chaveModelos],
  )

  const resultado = useMemo(
    () => avaliarLente(lente, entradas),
    [lente, entradas],
  )

  // O painel desmonta quando se fecha (e com ele o interruptor): uma
  // lente ligada não pode ficar no 3D sem ninguém a poder desligar.
  useEffect(
    () => () => {
      void limparLente()
    },
    [],
  )

  const lerModelos = async (): Promise<EntradaPesquisa[]> => {
    if (entradas.length > 0) return entradas
    setALer(true)
    setErro(null)
    const acumulado: EntradaPesquisa[] = []
    for (const m of models) {
      const modelo = viewerApi.getModel(m.id) as unknown as Parameters<
        typeof indexarModelo
      >[1] | null
      if (!modelo) continue
      try {
        await indexarModelo(m.id, modelo, (lote) => acumulado.push(...lote))
      } catch {
        setErro('Não foi possível ler um dos modelos por inteiro.')
      }
    }
    setLidas({ chave: chaveModelos, entradas: acumulado })
    setALer(false)
    return acumulado
  }

  // Modelos mudaram com a lente ligada: voltar a ler e a aplicar. A
  // primeira linha assíncrona afasta qualquer setState do corpo do
  // efeito.
  useEffect(() => {
    if (!activa || lidas.chave === '' || lidas.chave === chaveModelos) return
    let cancelado = false
    void (async () => {
      await Promise.resolve()
      if (cancelado) return
      const lista = await lerModelos()
      if (cancelado) return
      await aplicarLente(avaliarLente(lente, lista), lista, true)
    })()
    return () => {
      cancelado = true
    }
    // `lente`, `activa` e `lerModelos` são lidos no momento; o que
    // dispara a releitura é só a troca de modelos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chaveModelos])

  const actualizar = async (nova: Lente, ligar = activa) => {
    setLente(nova)
    if (!ligar) return
    const lista = await lerModelos()
    await aplicarLente(avaliarLente(nova, lista), lista, true)
  }

  const alternarActiva = async () => {
    if (activa) {
      await limparLente()
      setActiva(false)
      return
    }
    const lista = await lerModelos()
    await aplicarLente(avaliarLente(lente, lista), lista, true)
    setActiva(true)
  }

  const auto = async (eixo: EixoLente) => {
    const lista = await lerModelos()
    const nova = lenteAutomatica(eixo, lista)
    await actualizar(nova, activa)
  }

  const acrescentarRegra = () => {
    const nova: RegraLente = {
      id: `r-${Date.now()}`,
      nome: 'Regra',
      activa: true,
      eixo: 'classe',
      operador: 'igual',
      valor: '',
      accao: 'colorir',
      cor: corSeguinte(lente.regras),
    }
    void actualizar({ ...lente, regras: [...lente.regras, nova] })
  }

  const mudarRegra = (id: string, mudanca: Partial<RegraLente>) => {
    void actualizar({
      ...lente,
      regras: lente.regras.map((r) => (r.id === id ? { ...r, ...mudanca } : r)),
    })
  }

  const apagarRegra = (id: string) => {
    void actualizar({ ...lente, regras: lente.regras.filter((r) => r.id !== id) })
  }

  const guardar = () => {
    const nome = window.prompt('Nome da lente', lente.nome)
    if (!nome) return
    const nova = { ...lente, nome, id: `l-${Date.now()}` }
    const lista = [...guardadas.filter((l) => l.nome !== nome), nova]
    setGuardadas(lista)
    gravarLentes(lista)
    setLente(nova)
  }

  return (
    <div className="flex h-full flex-col gap-2 overflow-y-auto p-2 text-xs">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-[var(--color-text)]">
          Lentes
        </span>
        <button
          type="button"
          onClick={aoFechar}
          aria-label="Fechar as lentes"
          className="rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>

      <p className="text-[10px] leading-relaxed text-[var(--color-text-dim)]">
        Uma lente é uma lista de regras. A primeira que casa com um
        elemento é a que manda. O que não casar com nenhuma fica apagado,
        para o resto sobressair.
      </p>

      {models.length === 0 ? (
        <p className="rounded border border-[var(--color-border)] p-2 text-[var(--color-text-dim)]">
          Carrega um modelo para usar as lentes.
        </p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              onClick={() => void alternarActiva()}
              disabled={aLer}
              className={`rounded border px-2 py-1 font-medium ${
                activa
                  ? 'border-[var(--color-accent-border)] bg-[var(--color-accent-bg)] text-[var(--color-accent)]'
                  : 'border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-surface)]'
              }`}
            >
              {aLer ? 'A ler o modelo…' : activa ? 'Lente ligada' : 'Ligar lente'}
            </button>
            <button
              type="button"
              onClick={acrescentarRegra}
              className="flex items-center gap-1 rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
            >
              <Plus className="h-3 w-3" aria-hidden /> Regra
            </button>
            <button
              type="button"
              onClick={guardar}
              disabled={lente.regras.length === 0}
              className="rounded border border-[var(--color-border)] px-2 py-1 text-[var(--color-text-dim)] hover:text-[var(--color-text)] disabled:opacity-50"
            >
              Guardar
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-1 border-t border-[var(--color-border)] pt-2">
            <span className="flex items-center gap-1 text-[10px] text-[var(--color-text-dim)]">
              <Wand2 className="h-3 w-3" aria-hidden /> Automática por
            </span>
            {EIXOS.map((e) => (
              <button
                key={e.valor}
                type="button"
                onClick={() => void auto(e.valor)}
                className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
              >
                {e.label}
              </button>
            ))}
          </div>

          {guardadas.length > 0 && (
            <div className="flex flex-wrap items-center gap-1">
              <span className="text-[10px] text-[var(--color-text-dim)]">
                Guardadas:
              </span>
              {guardadas.map((l) => (
                <button
                  key={l.id}
                  type="button"
                  onClick={() => void actualizar(l)}
                  className="rounded border border-[var(--color-border)] px-2 py-0.5 text-[10px] text-[var(--color-text-dim)] hover:text-[var(--color-text)]"
                >
                  {l.nome}
                </button>
              ))}
            </div>
          )}

          {erro && <p className="text-[var(--color-warn-text)]">{erro}</p>}

          <ul className="space-y-1">
            {lente.regras.map((r) => (
              <li
                key={r.id}
                className="rounded border border-[var(--color-border)] p-1.5"
              >
                <div className="flex items-center gap-1">
                  <input
                    type="checkbox"
                    checked={r.activa}
                    onChange={(e) => mudarRegra(r.id, { activa: e.target.checked })}
                    aria-label={`Ligar a regra ${r.nome}`}
                  />
                  {r.accao === 'colorir' && (
                    <input
                      type="color"
                      value={r.cor}
                      onChange={(e) => mudarRegra(r.id, { cor: e.target.value })}
                      aria-label={`Cor da regra ${r.nome}`}
                      className="h-5 w-6 cursor-pointer border-0 bg-transparent p-0"
                    />
                  )}
                  <select
                    value={r.eixo}
                    onChange={(e) =>
                      mudarRegra(r.id, { eixo: e.target.value as EixoLente })
                    }
                    aria-label="Eixo"
                    className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                  >
                    {EIXOS.map((e) => (
                      <option key={e.valor} value={e.valor}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={r.operador}
                    onChange={(e) =>
                      mudarRegra(r.id, {
                        operador: e.target.value as OperadorLente,
                      })
                    }
                    aria-label="Operador"
                    className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                  >
                    {OPERADORES.map((o) => (
                      <option key={o.valor} value={o.valor}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => apagarRegra(r.id)}
                    aria-label={`Apagar a regra ${r.nome}`}
                    className="ml-auto rounded p-0.5 text-[var(--color-text-dim)] hover:text-[var(--color-error-text)]"
                  >
                    <Trash2 className="h-3 w-3" aria-hidden />
                  </button>
                </div>
                <div className="mt-1 flex items-center gap-1">
                  {r.operador !== 'vazio' && (
                    <input
                      value={r.valor}
                      onChange={(e) => mudarRegra(r.id, { valor: e.target.value })}
                      placeholder="IFCWALL, Parede…"
                      aria-label={`Valor da regra ${r.nome}`}
                      className="min-w-0 flex-1 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                    />
                  )}
                  <select
                    value={r.accao}
                    onChange={(e) =>
                      mudarRegra(r.id, { accao: e.target.value as AccaoLente })
                    }
                    aria-label="Ação"
                    className="rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-1 py-0.5"
                  >
                    {ACCOES.map((a) => (
                      <option key={a.valor} value={a.valor}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <span className="w-16 text-right tabular-nums text-[var(--color-text-dim)]">
                    {resultado.contagem[r.id] ?? 0}
                  </span>
                </div>
              </li>
            ))}
          </ul>

          {lente.regras.length > 0 && (
            <p className="border-t border-[var(--color-border)] pt-1 text-[10px] text-[var(--color-text-dim)]">
              {resultado.semRegra} sem regra, em cinzento.
              {entradas.length > 0 && ` ${entradas.length} elementos lidos.`}
            </p>
          )}
        </>
      )}
    </div>
  )
}
